import { useId, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import katex from 'katex'
import 'katex/dist/katex.min.css'
import { Button, Input } from '@arabquiz/ui'
import {
  ArrowDown,
  ArrowUp,
  ListChecks,
  ListOrdered,
  Library,
  Plus,
  Save,
  ToggleLeft,
  Trash2,
} from 'lucide-react'
import { McqOptionsEditor } from './question/McqOptionsEditor'
import { TfAnswerEditor } from './question/TfAnswerEditor'
import { OrderItemsEditor } from './question/OrderItemsEditor'
import {
  DEFAULT_TIME_SECONDS,
  MAX_TIME_SECONDS,
  MIN_TIME_SECONDS,
  makeMcq,
  makeOrder,
  makeTf,
  type EditorQuestion,
  type EditorQuiz,
} from './question/types'
import { AddFromBankPicker } from './question-bank/AddFromBankPicker'
import { bankItemToEditorQuestion } from './question-bank/types'

// Re-exported so existing route-level imports (`quizzes/new`, `$quizId/edit`)
// keep resolving `EditorQuiz` / `EditorQuestion` from this module unchanged
// after the E1 extraction.
export type { EditorQuestion, EditorQuiz }

/**
 * Editor for a single quiz — title, description and an ordered list of
 * questions. Wires the host UX for the two MVP question types (Multiple
 * Choice and True / False) and renders a KaTeX live preview of the
 * question prompt so authors can validate `$…$` / `$$…$$` math as they type.
 *
 * Why a single component instead of a route-level one? Both `new` and
 * `edit` pages share the same editing surface; the only difference is
 * what `initialQuiz` looks like and which mutation the parent fires on
 * submit. Keeping the component agnostic (it owns local state, exposes
 * `onSave(quiz)`) makes both routes essentially configuration shells.
 *
 * Visual direction: editorial-confidence carried over from the quiz list
 * page — Tajawal display headings (via `font-display`), oversized title
 * input, accent-rule above the H1, white-on-soft-blue card surface with a
 * `border-s-4 border-s-brand-500` leading stripe on every question card
 * (logical start-edge, so it lands on the visual right under `dir="rtl"`).
 * All spacing utilities are logical (`ps-*`, `pe-*`, `start-*`, `end-*`).
 */
type QuizEditorProps = {
  /** Page heading (e.g. "اختبار جديد" or "تعديل الاختبار"). */
  heading: string
  /** Seed state — empty for create, hydrated from `GET /:id` for edit. */
  initialQuiz: EditorQuiz
  /** Resolves when the underlying mutation finishes; throws on API failure. */
  onSave: (quiz: EditorQuiz) => Promise<void>
  /** Optional flag from the parent — wires the submit button busy state. */
  isSaving?: boolean
  /** Optional flag from the parent — surfaces the inline error banner. */
  isError?: boolean
}

const KATEX_MACROS = {
  '\\RR': '\\mathbb{R}',
  '\\NN': '\\mathbb{N}',
  '\\ZZ': '\\mathbb{Z}',
  '\\QQ': '\\mathbb{Q}',
  '\\CC': '\\mathbb{C}',
} as const

export function QuizEditor({
  heading,
  initialQuiz,
  onSave,
  isSaving = false,
  isError = false,
}: QuizEditorProps) {
  const { t } = useTranslation('quiz')
  const { t: tCommon } = useTranslation('common')

  const [title, setTitle] = useState(initialQuiz.title)
  const [description, setDescription] = useState(initialQuiz.description)
  const [questions, setQuestions] = useState<EditorQuestion[]>(initialQuiz.questions)

  // Add-from-bank picker (E4). Inserting bank items copies them into the
  // editor's question list (copy-on-insert) — later edits to either side
  // are independent.
  const [bankPickerOpen, setBankPickerOpen] = useState(false)

  // Title is required client-side too (server enforces 1-200 chars); we
  // toggle a small inline message once the field has been touched and
  // disable the submit button until the rule is satisfied.
  const [titleTouched, setTitleTouched] = useState(false)
  const titleInvalid = titleTouched && title.trim().length === 0

  const canSubmit = title.trim().length > 0 && !isSaving

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) {
      setTitleTouched(true)
      return
    }
    void onSave({
      title: title.trim(),
      description: description.trim(),
      questions,
    })
  }

  function addQuestion(make: () => EditorQuestion) {
    setQuestions((prev) => [...prev, make()])
  }

  // Append a batch of editor questions in one update (copy-on-insert from
  // the question bank). Reuses the same `questions` state the manual
  // add-question buttons drive, so inserted items behave identically.
  function appendQuestions(toAppend: EditorQuestion[]) {
    setQuestions((prev) => [...prev, ...toAppend])
  }

  function updateQuestion(index: number, next: EditorQuestion) {
    setQuestions((prev) => prev.map((q, i) => (i === index ? next : q)))
  }

  function removeQuestion(index: number) {
    setQuestions((prev) => prev.filter((_, i) => i !== index))
  }

  function moveQuestion(index: number, direction: -1 | 1) {
    setQuestions((prev) => {
      const target = index + direction
      if (target < 0 || target >= prev.length) return prev
      const next = prev.slice()
      const a = next[index]
      const b = next[target]
      if (a === undefined || b === undefined) return prev
      next[index] = b
      next[target] = a
      return next
    })
  }

  return (
    <main className="min-h-screen bg-gradient-to-bl from-brand-50/40 via-white to-white">
      <div className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-10">
          <div
            className="inline-block h-1 w-12 rounded-full bg-brand-500 mb-4"
            aria-hidden="true"
          />
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <h1 className="text-4xl sm:text-5xl font-bold font-display text-brand-900 tracking-tight">
              {heading}
            </h1>
            <a
              href="/quizzes"
              className="text-sm font-semibold text-brand-600 hover:text-brand-700 hover:underline self-start sm:self-auto"
            >
              {t('backToList')}
            </a>
          </div>
        </header>

        <form onSubmit={handleSubmit} noValidate>
          <section
            aria-labelledby="quiz-meta-heading"
            className="rounded-3xl border border-brand-100 bg-white shadow-sm p-6 sm:p-8 mb-8"
          >
            <h2
              id="quiz-meta-heading"
              className="text-xl font-bold font-display text-brand-900 tracking-tight mb-5"
            >
              {t('quizTitle')}
            </h2>

            <div className="space-y-5">
              <div>
                <label
                  htmlFor="quiz-title"
                  className="block text-sm font-semibold text-brand-900 mb-1.5"
                >
                  {t('quizTitle')}
                </label>
                <Input
                  id="quiz-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onBlur={() => setTitleTouched(true)}
                  placeholder={t('quizTitlePlaceholder')}
                  maxLength={200}
                  className="h-12 text-base"
                  aria-invalid={titleInvalid || undefined}
                  aria-describedby={titleInvalid ? 'quiz-title-error' : undefined}
                />
                {titleInvalid ? (
                  <p
                    id="quiz-title-error"
                    className="text-xs text-red-600 mt-1.5"
                    role="alert"
                  >
                    {t('quizTitle')}
                  </p>
                ) : null}
              </div>

              <div>
                <label
                  htmlFor="quiz-description"
                  className="block text-sm font-semibold text-brand-900 mb-1.5"
                >
                  {t('description')}
                </label>
                <textarea
                  id="quiz-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('descriptionPlaceholder')}
                  maxLength={2000}
                  rows={3}
                  className="block w-full rounded-md border border-brand-200 bg-white ps-3 pe-3 py-2 text-sm text-brand-900 placeholder:text-brand-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 resize-y"
                />
              </div>
            </div>
          </section>

          <section aria-labelledby="quiz-questions-heading" className="mb-8">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-5">
              <h2
                id="quiz-questions-heading"
                className="text-xl font-bold font-display text-brand-900 tracking-tight"
              >
                {t('question')}
              </h2>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => addQuestion(makeMcq)}
                  className="gap-2"
                >
                  <ListChecks className="h-4 w-4" aria-hidden="true" />
                  <span>{t('addMcqQuestion')}</span>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => addQuestion(makeTf)}
                  className="gap-2"
                >
                  <ToggleLeft className="h-4 w-4" aria-hidden="true" />
                  <span>{t('addTfQuestion')}</span>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => addQuestion(makeOrder)}
                  className="gap-2"
                >
                  <ListOrdered className="h-4 w-4" aria-hidden="true" />
                  <span>{t('addOrderQuestion')}</span>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setBankPickerOpen(true)}
                  className="gap-2"
                >
                  <Library className="h-4 w-4" aria-hidden="true" />
                  <span>{t('addFromBank')}</span>
                </Button>
              </div>
            </div>

            <p className="text-xs text-brand-600 mb-4 leading-relaxed">
              {t('mathHint')}
            </p>

            {questions.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-brand-200 bg-white/60 px-6 py-12 text-center">
                <div
                  className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-brand-50 ring-1 ring-brand-200/60"
                  aria-hidden="true"
                >
                  <Plus className="h-7 w-7 text-brand-600" strokeWidth={1.5} />
                </div>
                <p className="text-brand-700">{t('noQuestionsYet')}</p>
              </div>
            ) : (
              <ol className="space-y-5">
                {questions.map((question, index) => (
                  <li key={`q-${index}`}>
                    <QuestionCard
                      question={question}
                      index={index}
                      total={questions.length}
                      onChange={(next) => updateQuestion(index, next)}
                      onRemove={() => removeQuestion(index)}
                      onMoveUp={() => moveQuestion(index, -1)}
                      onMoveDown={() => moveQuestion(index, 1)}
                    />
                  </li>
                ))}
              </ol>
            )}
          </section>

          {isError ? (
            <div
              className="rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 mb-6"
              role="alert"
            >
              {t('saveError')}
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-3">
            <a
              href="/quizzes"
              className="text-sm font-semibold text-brand-600 hover:text-brand-700 hover:underline"
            >
              {tCommon('cancel')}
            </a>
            <Button
              type="submit"
              size="lg"
              disabled={!canSubmit}
              className="gap-2"
            >
              <Save className="h-5 w-5" aria-hidden="true" />
              <span>{isSaving ? tCommon('loading') : t('saveQuiz')}</span>
            </Button>
          </div>
        </form>
      </div>

      <AddFromBankPicker
        open={bankPickerOpen}
        onClose={() => setBankPickerOpen(false)}
        onInsert={(items) => {
          appendQuestions(items.map(bankItemToEditorQuestion))
          setBankPickerOpen(false)
        }}
      />
    </main>
  )
}

type QuestionCardProps = {
  question: EditorQuestion
  index: number
  total: number
  onChange: (next: EditorQuestion) => void
  onRemove: () => void
  onMoveUp: () => void
  onMoveDown: () => void
}

/**
 * One editable question. Renders the right inner editor based on the
 * discriminator (`type`) and shows the KaTeX live preview underneath
 * the question text input — both reading from the same `question.text`
 * so the preview stays in lockstep with edits without an explicit state.
 */
function QuestionCard({
  question,
  index,
  total,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: QuestionCardProps) {
  const { t } = useTranslation('quiz')
  const textInputId = useId()
  const timeInputId = useId()
  const explanationInputId = useId()

  const previewHtml = useMemo(() => renderMathPreview(question.text), [question.text])

  return (
    <article className="rounded-2xl border border-brand-100 bg-white shadow-sm border-s-4 border-s-brand-500 ps-6 pe-5 py-5">
      <header className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-brand-500 text-white text-sm font-bold font-display">
            {index + 1}
          </span>
          <span className="text-sm font-semibold text-brand-900">
            {t('questionNumber', { n: index + 1 })}
          </span>
          {question.type === 'MultipleChoice' ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700">
              <ListChecks className="h-3.5 w-3.5" aria-hidden="true" />
              {t('mcqBadge')}
            </span>
          ) : question.type === 'TrueFalse' ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
              <ToggleLeft className="h-3.5 w-3.5" aria-hidden="true" />
              {t('tfBadge')}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
              <ListOrdered className="h-3.5 w-3.5" aria-hidden="true" />
              {t('orderBadge')}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={index === 0}
            aria-label={t('moveUp')}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-brand-600 hover:bg-brand-50 hover:text-brand-800 disabled:opacity-30 disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <ArrowUp className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={index === total - 1}
            aria-label={t('moveDown')}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-brand-600 hover:bg-brand-50 hover:text-brand-800 disabled:opacity-30 disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <ArrowDown className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={onRemove}
            aria-label={t('deleteQuestion')}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-red-600 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </header>

      <div className="space-y-4">
        <div>
          <label
            htmlFor={textInputId}
            className="block text-sm font-semibold text-brand-900 mb-1.5"
          >
            {t('questionText')}
          </label>
          <textarea
            id={textInputId}
            value={question.text}
            onChange={(e) => onChange({ ...question, text: e.target.value })}
            placeholder={t('questionTextPlaceholder')}
            maxLength={500}
            rows={2}
            className="block w-full rounded-md border border-brand-200 bg-white ps-3 pe-3 py-2 text-sm text-brand-900 placeholder:text-brand-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 resize-y"
          />
          {question.text.trim().length > 0 ? (
            <div className="mt-3">
              <p className="text-xs font-semibold text-brand-600 mb-1.5">
                {t('preview')}
              </p>
              <div
                className="rounded-md bg-brand-50/60 ps-3 pe-3 py-2.5 text-base text-brand-900 leading-relaxed"
                // KaTeX produces sanitised HTML when called with
                // `trust: false` and `strict: true`; we never pass user
                // markup through unescaped except for the math spans
                // KaTeX itself emits.
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
          ) : null}
        </div>

        <div>
          <label
            htmlFor={timeInputId}
            className="block text-sm font-semibold text-brand-900 mb-1.5"
          >
            {t('timeSeconds')}
          </label>
          <Input
            id={timeInputId}
            type="number"
            min={MIN_TIME_SECONDS}
            max={MAX_TIME_SECONDS}
            dir="ltr"
            value={question.timeSeconds}
            onChange={(e) => {
              const parsed = Number.parseInt(e.target.value, 10)
              const clamped = Number.isFinite(parsed)
                ? Math.max(MIN_TIME_SECONDS, Math.min(MAX_TIME_SECONDS, parsed))
                : DEFAULT_TIME_SECONDS
              onChange({ ...question, timeSeconds: clamped })
            }}
            className="w-32 text-center"
          />
        </div>

        {question.type === 'MultipleChoice' ? (
          <McqOptionsEditor question={question} onChange={onChange} />
        ) : question.type === 'TrueFalse' ? (
          <TfAnswerEditor question={question} onChange={onChange} />
        ) : (
          <OrderItemsEditor question={question} onChange={onChange} />
        )}

        <div>
          <label
            htmlFor={explanationInputId}
            className="block text-sm font-semibold text-brand-900 mb-1.5"
          >
            {t('explanation')}
          </label>
          <textarea
            id={explanationInputId}
            value={question.explanation ?? ''}
            onChange={(e) => onChange({ ...question, explanation: e.target.value })}
            placeholder={t('explanationPlaceholder')}
            maxLength={1000}
            rows={2}
            className="block w-full rounded-md border border-brand-200 bg-white ps-3 pe-3 py-2 text-sm text-brand-900 placeholder:text-brand-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 resize-y"
          />
        </div>
      </div>
    </article>
  )
}

/**
 * Render a quiz question prompt with inline / display KaTeX math
 * substitutions. Plain text outside the delimiters is HTML-escaped so a
 * pasted `<script>` lands as literal text in the preview pane, not as
 * executable markup.
 *
 * KaTeX is invoked with `trust: false` and `strict: true` so commands
 * like `\href` (which would let an author smuggle URLs into the preview)
 * and undefined symbols are refused — KaTeX throws and we surface a
 * localised "[خطأ في الصيغة]" badge instead of failing the whole render.
 */
function renderMathPreview(text: string): string {
  const segments: string[] = []
  // Walk the input one math token at a time. The delimiter order
  // matters: `$$…$$` must be checked BEFORE single `$…$` so we do not
  // misread a display-mode opener as a pair of inline tokens.
  const pattern = /\$\$([\s\S]+?)\$\$|\$([^$\n]+?)\$/g
  let lastIndex = 0
  let match: RegExpExecArray | null = pattern.exec(text)
  while (match !== null) {
    if (match.index > lastIndex) {
      segments.push(escapeHtml(text.slice(lastIndex, match.index)))
    }
    const displayMath = match[1]
    const inlineMath = match[2]
    if (typeof displayMath === 'string') {
      segments.push(renderMathSafe(displayMath, true))
    } else if (typeof inlineMath === 'string') {
      segments.push(renderMathSafe(inlineMath, false))
    }
    lastIndex = pattern.lastIndex
    match = pattern.exec(text)
  }
  if (lastIndex < text.length) {
    segments.push(escapeHtml(text.slice(lastIndex)))
  }
  return segments.join('').replace(/\n/g, '<br>')
}

function renderMathSafe(math: string, displayMode: boolean): string {
  try {
    return katex.renderToString(math, {
      displayMode,
      macros: KATEX_MACROS,
      trust: false,
      strict: true,
      throwOnError: true,
    })
  } catch {
    return '<span class="text-red-500 text-sm">[خطأ في الصيغة]</span>'
  }
}

/**
 * Minimal HTML escape — sufficient because we only inject the result into
 * an attribute-free text context and KaTeX itself emits balanced markup.
 */
function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}
